/**
 * PTEL Andalucía - Sistema Unificado de Códigos INE
 * 
 * ⚠️ FUENTE ÚNICA DE VERDAD para códigos INE de municipios andaluces
 * 
 * Este módulo deriva TODOS los códigos INE desde municipiosCentroides.ts,
 * que fue generado automáticamente desde el servicio WFS DERA del IECA.
 * 
 * NUNCA editar manualmente los códigos aquí. Si hay errores:
 * 1. Verificar contra https://www.ine.es/daco/daco42/codmun/
 * 2. Corregir municipiosCentroides.ts (fuente primaria)
 * 3. Este archivo se actualiza automáticamente
 * 
 * @module codigosINEUnificado
 * @version 1.0.0
 * @date Diciembre 2025
 * @author Proyecto PTEL - Normalizador de Coordenadas
 */

import { CENTROIDES_MUNICIPIOS } from './municipiosCentroides';

// ============================================================================
// TIPOS
// ============================================================================

export interface MunicipioINEUnificado {
  codigo: string;
  nombre: string;
  nombreNormalizado: string;
  provincia: string;
  codigoProvincia: string;
}

export interface ResultadoBusquedaINE {
  encontrado: boolean;
  codigo: string | null;
  nombre: string | null;
  provincia: string | null;
  confianza: 'EXACTA' | 'PARCIAL' | 'NINGUNA';
}

// ============================================================================
// CONSTANTES DE PROVINCIAS
// ============================================================================

export const PROVINCIAS_ANDALUCIA: Record<string, string> = {
  '04': 'Almería',
  '11': 'Cádiz',
  '14': 'Córdoba',
  '18': 'Granada',
  '21': 'Huelva',
  '23': 'Jaén',
  '29': 'Málaga',
  '41': 'Sevilla'
};

/**
 * Deriva la provincia andaluza a partir de un código INE de 5 dígitos.
 * El prefijo de 2 dígitos determina la provincia sin ambigüedad
 * (04/11/14/18/21/23/29/41). Devuelve `undefined` si el código es nulo,
 * vacío o su prefijo no corresponde a una provincia andaluza — nunca
 * inventa un valor (OBS-P1-PROVINCIA-FALLBACK-GRANADA · HX-FALLBACK-ENMASCARA-BUG).
 */
export function getProvinciaPorCodigoINE(
  codigo: string | null | undefined
): string | undefined {
  if (!codigo) return undefined;
  return PROVINCIAS_ANDALUCIA[codigo.trim().slice(0, 2)];
}

export const CODIGO_PROVINCIA_POR_NOMBRE: Record<string, string> = {
  'almeria': '04',
  'cadiz': '11',
  'cordoba': '14',
  'granada': '18',
  'huelva': '21',
  'jaen': '23',
  'malaga': '29',
  'sevilla': '41'
};

// ============================================================================
// FUNCIONES DE NORMALIZACIÓN
// ============================================================================

/**
 * Normaliza un nombre de municipio para búsqueda
 * - Convierte a minúsculas
 * - Elimina acentos
 * - Elimina artículos iniciales (el, la, los, las)
 * - Normaliza espacios
 */
export function normalizarNombreMunicipio(nombre: string): string {
  if (!nombre) return '';
  
  return nombre
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Eliminar acentos
    .replace(/^(el|la|los|las)\s+/i, '') // Eliminar artículos iniciales
    .replace(/\s+/g, ' ')
    .trim();
}

// ============================================================================
// ÍNDICES DERIVADOS (generados automáticamente desde CENTROIDES_MUNICIPIOS)
// ============================================================================

/**
 * Mapa de código INE → información del municipio
 * Derivado de CENTROIDES_MUNICIPIOS
 */
export const MUNICIPIOS_POR_CODIGO: Map<string, MunicipioINEUnificado> = new Map(
  Object.entries(CENTROIDES_MUNICIPIOS).map(([codigo, data]) => [
    codigo,
    {
      codigo,
      nombre: data.nombre,
      nombreNormalizado: normalizarNombreMunicipio(data.nombre),
      provincia: data.provincia,
      codigoProvincia: codigo.substring(0, 2)
    }
  ])
);

/**
 * Mapa de nombre normalizado → código INE
 * Para búsqueda rápida por nombre
 */
export const CODIGO_POR_NOMBRE: Map<string, string> = new Map(
  Array.from(MUNICIPIOS_POR_CODIGO.entries()).map(([codigo, data]) => [
    data.nombreNormalizado,
    codigo
  ])
);

/**
 * Set de códigos INE válidos para validación O(1)
 */
export const CODIGOS_INE_VALIDOS: Set<string> = new Set(
  MUNICIPIOS_POR_CODIGO.keys()
);

// ============================================================================
// FUNCIONES DE BÚSQUEDA Y VALIDACIÓN
// ============================================================================

/**
 * Obtiene el código INE de un municipio por su nombre
 * @param nombre - Nombre del municipio (acepta variaciones)
 * @returns Código INE de 5 dígitos o null si no se encuentra
 */
export function getCodigoINE(nombre: string): string | null {
  const normalizado = normalizarNombreMunicipio(nombre);
  return CODIGO_POR_NOMBRE.get(normalizado) || null;
}

/**
 * Valida si un código INE existe en el catálogo oficial
 * @param codigo - Código INE a validar
 * @returns true si el código existe
 */
export function esCodigoINEValido(codigo: string): boolean {
  return CODIGOS_INE_VALIDOS.has(codigo);
}

/**
 * Obtiene información completa de un municipio por código INE
 * @param codigo - Código INE de 5 dígitos
 * @returns Información del municipio o null
 */
export function getMunicipioPorCodigo(codigo: string): MunicipioINEUnificado | null {
  return MUNICIPIOS_POR_CODIGO.get(codigo) || null;
}

/**
 * Busca un municipio por nombre con diferentes niveles de confianza
 * @param nombre - Nombre a buscar
 * @returns Resultado con nivel de confianza
 */
export function buscarMunicipio(nombre: string): ResultadoBusquedaINE {
  const normalizado = normalizarNombreMunicipio(nombre);
  
  // Búsqueda exacta
  const codigoExacto = CODIGO_POR_NOMBRE.get(normalizado);
  if (codigoExacto) {
    const municipio = MUNICIPIOS_POR_CODIGO.get(codigoExacto)!;
    return {
      encontrado: true,
      codigo: codigoExacto,
      nombre: municipio.nombre,
      provincia: municipio.provincia,
      confianza: 'EXACTA'
    };
  }
  
  // Búsqueda parcial (el nombre buscado está contenido en algún municipio)
  for (const [nombreMun, codigo] of CODIGO_POR_NOMBRE.entries()) {
    if (nombreMun.includes(normalizado) || normalizado.includes(nombreMun)) {
      const municipio = MUNICIPIOS_POR_CODIGO.get(codigo)!;
      return {
        encontrado: true,
        codigo,
        nombre: municipio.nombre,
        provincia: municipio.provincia,
        confianza: 'PARCIAL'
      };
    }
  }
  
  return {
    encontrado: false,
    codigo: null,
    nombre: null,
    provincia: null,
    confianza: 'NINGUNA'
  };
}

/**
 * P1-S3 (esc. 4): candidato de municipio para el confirmador bloqueante.
 */
export interface CandidatoMunicipio {
  codigo: string;
  nombre: string;
  provincia: string;
}

/**
 * P1-S3 (esc. 4): devuelve TODOS los municipios candidatos próximos a un
 * nombre, con provincia — a diferencia de buscarMunicipio(), que devuelve
 * solo el primer match parcial y enmascara homónimos.
 *
 * Criterios (sobre nombre normalizado NFD sin artículos):
 *  1. Igualdad exacta (primero en la lista).
 *  2. Contención bidireccional («mancha» → «Mancha Real»).
 *  3. Prefijo común ≥4 caracteres y ≥60 % del nombre más corto
 *     (caso de libro: «Colomers» → «Colomera», que la contención no capta).
 */
export function buscarCandidatosMunicipio(nombre: string, limite = 8): CandidatoMunicipio[] {
  const normalizado = normalizarNombreMunicipio(nombre);
  if (normalizado.length < 2) return [];

  const exactos: CandidatoMunicipio[] = [];
  const proximos: CandidatoMunicipio[] = [];

  for (const m of MUNICIPIOS_POR_CODIGO.values()) {
    const cand = { codigo: m.codigo, nombre: m.nombre, provincia: m.provincia };
    if (m.nombreNormalizado === normalizado) {
      exactos.push(cand);
      continue;
    }
    const contenido =
      m.nombreNormalizado.includes(normalizado) ||
      normalizado.includes(m.nombreNormalizado);
    if (contenido) {
      proximos.push(cand);
      continue;
    }
    // Prefijo común
    let p = 0;
    const min = Math.min(m.nombreNormalizado.length, normalizado.length);
    while (p < min && m.nombreNormalizado[p] === normalizado[p]) p++;
    if (p >= 4 && p >= min * 0.6) proximos.push(cand);
  }

  proximos.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
  return [...exactos, ...proximos].slice(0, limite);
}

/**
 * Obtiene todos los municipios de una provincia
 * @param codigoProvincia - Código de 2 dígitos de la provincia
 * @returns Array de municipios
 */
export function getMunicipiosPorProvincia(codigoProvincia: string): MunicipioINEUnificado[] {
  return Array.from(MUNICIPIOS_POR_CODIGO.values())
    .filter(m => m.codigoProvincia === codigoProvincia);
}

/**
 * Obtiene estadísticas del catálogo
 */
export function getEstadisticasCatalogo(): {
  totalMunicipios: number;
  porProvincia: Record<string, number>;
} {
  const porProvincia: Record<string, number> = {};
  
  for (const municipio of MUNICIPIOS_POR_CODIGO.values()) {
    const prov = municipio.provincia;
    porProvincia[prov] = (porProvincia[prov] || 0) + 1;
  }
  
  return {
    totalMunicipios: MUNICIPIOS_POR_CODIGO.size,
    porProvincia
  };
}

// ============================================================================
// VALIDACIÓN Y CORRECCIÓN DE CÓDIGOS INE
// ============================================================================

/**
 * Resultado de validación y corrección de código INE
 */
export interface ResultadoValidacionINE {
  /** Código INE válido (original si era válido, corregido si se pudo corregir) */
  codigo: string | null;
  /** Nombre oficial del municipio según catálogo INE */
  nombreOficial: string | null;
  /** Provincia del municipio */
  provincia: string | null;
  /** Si el código original era válido */
  esValido: boolean;
  /** Si el código fue corregido automáticamente */
  fueCorregido: boolean;
  /** Código original (antes de corrección) */
  codigoOriginal: string;
  /** Nivel de confianza de la corrección */
  confianza: 'VALIDADO' | 'CORREGIDO_EXACTO' | 'CORREGIDO_PARCIAL' | 'NO_CORREGIBLE';
  /** Advertencia o mensaje informativo */
  advertencia: string | null;
}

/**
 * Valida un código INE y lo corrige automáticamente si es posible
 * 
 * Esta función debe usarse SIEMPRE al procesar códigos INE extraídos de documentos PTEL.
 * Los documentos pueden contener errores de transcripción o códigos que corresponden
 * a municipios diferentes al indicado.
 * 
 * Flujo de validación:
 * 1. Verifica si el código existe en el catálogo INE oficial
 * 2. Si existe Y el nombre coincide → VALIDADO
 * 3. Si existe PERO el nombre NO coincide → Corrige buscando por nombre
 * 4. Si NO existe → Intenta corregir buscando por nombre del municipio
 * 
 * @param codigoExtraido - Código INE extraído del documento (puede ser inválido o incorrecto)
 * @param nombreMunicipio - Nombre del municipio para validación cruzada y corrección
 * @returns Resultado con código validado/corregido e información de la operación
 * 
 * @example
 * // Código válido y consistente con nombre
 * validarYCorregirCodigoINE('18011', 'Alfacar')
 * // → { codigo: '18011', esValido: true, fueCorregido: false, confianza: 'VALIDADO' }
 * 
 * @example
 * // Código válido pero inconsistente (18903 es Nevada, no Valle del Zalabí)
 * validarYCorregirCodigoINE('18903', 'Valle del Zalabí')
 * // → { codigo: '18907', esValido: false, fueCorregido: true, confianza: 'CORREGIDO_EXACTO',
 * //     advertencia: 'INE corregido: 18903 (Nevada) → 18907 (Valle del Zalabí)' }
 * 
 * @example
 * // Código inexistente, corregible por nombre
 * validarYCorregirCodigoINE('18009', 'Alfacar')
 * // → { codigo: '18011', esValido: false, fueCorregido: true, confianza: 'CORREGIDO_EXACTO' }
 * 
 * @example
 * // Código inválido, no corregible
 * validarYCorregirCodigoINE('99999', 'MunicipioInventado')
 * // → { codigo: null, esValido: false, fueCorregido: false, confianza: 'NO_CORREGIBLE' }
 */
export function validarYCorregirCodigoINE(
  codigoExtraido: string,
  nombreMunicipio?: string
): ResultadoValidacionINE {
  // Normalizar código (asegurar 5 dígitos)
  const codigoNormalizado = codigoExtraido?.trim().padStart(5, '0') || '';
  
  // Resultado base
  const resultado: ResultadoValidacionINE = {
    codigo: null,
    nombreOficial: null,
    provincia: null,
    esValido: false,
    fueCorregido: false,
    codigoOriginal: codigoExtraido || '',
    confianza: 'NO_CORREGIBLE',
    advertencia: null
  };
  
  // 1. Verificar si el código existe
  const municipioDelCodigo = esCodigoINEValido(codigoNormalizado) 
    ? getMunicipioPorCodigo(codigoNormalizado) 
    : null;
  
  // 2. Si existe el código, verificar consistencia con nombre
  if (municipioDelCodigo) {
    // Si no hay nombre para validar, aceptar el código tal cual
    if (!nombreMunicipio) {
      resultado.codigo = codigoNormalizado;
      resultado.nombreOficial = municipioDelCodigo.nombre;
      resultado.provincia = municipioDelCodigo.provincia;
      resultado.esValido = true;
      resultado.confianza = 'VALIDADO';
      return resultado;
    }
    
    // Verificar si el nombre coincide con el código
    const nombreNormDoc = normalizarNombreMunicipio(nombreMunicipio);
    const nombreNormCat = municipioDelCodigo.nombreNormalizado;
    
    const nombreCoincide = 
      nombreNormDoc === nombreNormCat ||
      nombreNormDoc.includes(nombreNormCat) ||
      nombreNormCat.includes(nombreNormDoc);
    
    if (nombreCoincide) {
      // Código y nombre coinciden → VALIDADO
      resultado.codigo = codigoNormalizado;
      resultado.nombreOficial = municipioDelCodigo.nombre;
      resultado.provincia = municipioDelCodigo.provincia;
      resultado.esValido = true;
      resultado.confianza = 'VALIDADO';
      return resultado;
    }
    
    // 3. Código existe pero nombre NO coincide → Buscar el código correcto por nombre
    const busqueda = buscarMunicipio(nombreMunicipio);
    
    if (busqueda.encontrado && busqueda.codigo && busqueda.codigo !== codigoNormalizado) {
      resultado.codigo = busqueda.codigo;
      resultado.nombreOficial = busqueda.nombre;
      resultado.provincia = busqueda.provincia;
      resultado.fueCorregido = true;
      resultado.confianza = busqueda.confianza === 'EXACTA' 
        ? 'CORREGIDO_EXACTO' 
        : 'CORREGIDO_PARCIAL';
      resultado.advertencia = 
        `INE corregido: ${codigoExtraido} (${municipioDelCodigo.nombre}) → ` +
        `${busqueda.codigo} (${busqueda.nombre})`;
      return resultado;
    }
    
    // El código existe pero el nombre del documento no se encuentra
    // Mantener el código original pero advertir
    resultado.codigo = codigoNormalizado;
    resultado.nombreOficial = municipioDelCodigo.nombre;
    resultado.provincia = municipioDelCodigo.provincia;
    resultado.esValido = true;
    resultado.confianza = 'VALIDADO';
    resultado.advertencia = 
      `Posible inconsistencia: código ${codigoNormalizado} es "${municipioDelCodigo.nombre}" ` +
      `pero documento indica "${nombreMunicipio}" (no encontrado en catálogo)`;
    return resultado;
  }
  
  // 4. El código NO existe - intentar corrección por nombre
  if (!nombreMunicipio) {
    resultado.advertencia = 
      `Código INE inválido: ${codigoExtraido}. No se puede corregir sin nombre de municipio.`;
    return resultado;
  }
  
  // Buscar por nombre
  const busqueda = buscarMunicipio(nombreMunicipio);
  
  if (busqueda.encontrado && busqueda.codigo) {
    resultado.codigo = busqueda.codigo;
    resultado.nombreOficial = busqueda.nombre;
    resultado.provincia = busqueda.provincia;
    resultado.fueCorregido = true;
    resultado.confianza = busqueda.confianza === 'EXACTA' 
      ? 'CORREGIDO_EXACTO' 
      : 'CORREGIDO_PARCIAL';
    resultado.advertencia = 
      `INE corregido: ${codigoExtraido} (inválido) → ${busqueda.codigo} (${busqueda.nombre})`;
    
    return resultado;
  }
  
  // 5. No se pudo corregir
  resultado.advertencia = 
    `Código INE inválido: ${codigoExtraido} para "${nombreMunicipio}". ` +
    `No se encontró en el catálogo INE oficial.`;
  
  return resultado;
}

/**
 * Valida un lote de códigos INE y genera un informe
 * 
 * @param registros - Array de objetos con codigo y nombre de municipio
 * @returns Informe con estadísticas y detalles de cada validación
 */
export function validarLoteCodigosINE(
  registros: Array<{ codigo: string; nombreMunicipio: string }>
): {
  total: number;
  validos: number;
  corregidos: number;
  noCorregibles: number;
  tasaExito: number;
  detalles: ResultadoValidacionINE[];
} {
  const detalles = registros.map(r => 
    validarYCorregirCodigoINE(r.codigo, r.nombreMunicipio)
  );
  
  const validos = detalles.filter(d => d.esValido && !d.fueCorregido).length;
  const corregidos = detalles.filter(d => d.fueCorregido).length;
  const noCorregibles = detalles.filter(d => !d.codigo).length;
  const total = registros.length;
  
  return {
    total,
    validos,
    corregidos,
    noCorregibles,
    tasaExito: total > 0 ? ((validos + corregidos) / total) * 100 : 0,
    detalles
  };
}

// ============================================================================
// VALIDACIÓN EN TIEMPO DE CARGA
// ============================================================================

// Verificar integridad del catálogo al cargar el módulo
const stats = getEstadisticasCatalogo();
if (stats.totalMunicipios < 780) {
  console.warn(
    `[codigosINEUnificado] ⚠️ Catálogo incompleto: ${stats.totalMunicipios} municipios ` +
    `(esperados ~785). Verificar municipiosCentroides.ts`
  );
}

// Exportar estadísticas para debugging
export const CATALOGO_STATS = stats;
